import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Trash2, Save } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  getFaq, updateFaq, deleteFaq,
  getPixel, updatePixel, deletePixel,
  getRichtext, updateRichtext, deleteRichtext,
  getGallery, updateGallery, deleteGallery,
  listMedia, BACKEND_URL,
} from "@/lib/api";
import PortalNotesFields from "@/components/PortalNotesFields";
import RichTextEditor from "@/components/RichTextEditor";
import RichTextView from "@/components/previews/RichTextView";
import FAQView from "@/components/previews/FAQView";
import GalleryView from "@/components/previews/GalleryView";
import MediaUploadButton from "@/components/MediaUploadButton";
import { Plus, Trash as TrashIcon, ArrowUp, ArrowDown, GripVertical } from "lucide-react";

const LABELS = {
  faq: "FAQ",
  ps_pixel: "PortalStudio Pixel",
  ga_pixel: "GA4 Pixel",
  richtext: "Rich Text",
  gallery: "Gallery",
};

const FETCHERS = {
  faq: getFaq, ps_pixel: getPixel, ga_pixel: getPixel, richtext: getRichtext,
  gallery: getGallery,
};
const UPDATERS = {
  faq: updateFaq, ps_pixel: updatePixel, ga_pixel: updatePixel, richtext: updateRichtext,
  gallery: updateGallery,
};
const DELETERS = {
  faq: deleteFaq, ps_pixel: deletePixel, ga_pixel: deletePixel, richtext: deleteRichtext,
  gallery: deleteGallery,
};

export default function EditComponent() {
  const { type, id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mediaMap, setMediaMap] = useState({});

  useEffect(() => {
    let c = false;
    const f = FETCHERS[type];
    if (!f) return;
    f(id).then((d) => { if (!c) setDoc(d); })
      .catch(() => toast.error("Not found or you don't have access"))
      .finally(() => !c && setLoading(false));
    if (type === "gallery") {
      listMedia().then((m) => {
        if (c) return;
        const map = {};
        for (const it of (m.media || [])) map[it.id] = { ...it, url: `${BACKEND_URL}/api/media/${it.id}.${it.ext}` };
        setMediaMap(map);
      }).catch(() => {});
    }
    return () => { c = true; };
  }, [type, id]);

  const set = (patch) => setDoc((prev) => ({ ...prev, ...patch }));
  const setItems = (itemsOrFn) =>
    setDoc((prev) => ({ ...prev, items: typeof itemsOrFn === "function" ? itemsOrFn(prev.items || []) : itemsOrFn }));

  const onSave = async () => {
    if (!doc) return;
    setSaving(true);
    try {
      const u = UPDATERS[type];
      const payload = { ...doc };
      delete payload.id; delete payload.customer_id; delete payload.created_by;
      delete payload.created_at; delete payload.updated_at; delete payload._id;
      delete payload.hit_count; delete payload.has_api_secret;
      await u(id, payload);
      toast.success("Saved");
      nav("/dashboard");
    } catch (err) {
      if (err?.response?.status === 413) toast.error("Content too large");
      else toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete this ${LABELS[type] || "component"}? This cannot be undone.`)) return;
    try {
      await DELETERS[type](id);
      toast.success("Deleted");
      nav("/dashboard");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAFA]"><Navbar />
      <div className="grid place-items-center h-64 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
    </div>
  );
  if (!doc) return (
    <div className="min-h-screen bg-[#FAFAFA]"><Navbar />
      <div className="text-center text-zinc-500 mt-20 text-sm">Component not found.</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950 mb-6"
              data-testid="edit-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>

        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-3">Edit</div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold text-zinc-950">
              {doc.title || doc.name || LABELS[type]}
            </h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onDelete} data-testid="edit-delete-btn"
              className="text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200">
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </Button>
            <Button onClick={onSave} disabled={saving} data-testid="edit-save-btn"
              className="bg-zinc-950 text-white hover:bg-zinc-800">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save changes
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-7 space-y-8">
            {/* ---- Per-type fields ---- */}
            {type === "faq" && <FaqFields doc={doc} set={set} setItems={setItems} />}
            {(type === "ps_pixel" || type === "ga_pixel") && <PixelFields doc={doc} set={set} kind={type} />}
            {type === "richtext" && <RichTextFields doc={doc} set={set} />}
            {type === "gallery" && <GalleryFields doc={doc} set={set} setItems={setItems} mediaMap={mediaMap} setMediaMap={setMediaMap} />}

            <PortalNotesFields
              portals={doc.portals || []} setPortals={(p) => set({ portals: p })}
              notes={doc.notes || ""} setNotes={(n) => set({ notes: n })}
              tags={doc.tags || []} setTags={(t) => set({ tags: t })}
              folderId={doc.folder_id || null} setFolderId={(f) => set({ folder_id: f })}
            />
          </div>

          <div className="md:col-span-5">
            <div className="md:sticky md:top-24">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-3">Live Preview</div>
              <div className="border border-zinc-200 rounded-md bg-white overflow-hidden">
                <div className="border-b border-zinc-100 px-4 py-2 flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                  <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                  <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                  <span className="ml-3 text-[11px] font-mono text-zinc-400">edit/{type}/{id}</span>
                </div>
                <div className="p-6 bg-white min-h-[420px]">
                  {type === "faq" && <FAQView data={doc} />}
                  {type === "richtext" && <RichTextView data={doc} />}
                  {type === "gallery" && <GalleryView data={doc} mediaMap={mediaMap} />}
                  {(type === "ps_pixel" || type === "ga_pixel") && (
                    <div className="text-sm text-zinc-500">
                      Pixel analytics: <Link to={`/pixel/${id}/analytics`} className="underline">view dashboard</Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="border border-zinc-200 bg-white p-6 rounded-md space-y-5">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">{title}</div>
      {children}
    </section>
  );
}

function FaqFields({ doc, set, setItems }) {
  const addItem = () => setItems((l) => [...(l || []), { question: "", answer: "" }]);
  const removeItem = (i) => setItems((l) => l.filter((_, idx) => idx !== i));
  const updateItem = (i, field, value) =>
    setItems((l) => l.map((it, idx) => idx === i ? { ...it, [field]: value } : it));
  return (
    <>
      <Section title="Heading">
        <div><Label className="text-xs text-zinc-500">Title</Label>
          <Input value={doc.title || ""} onChange={(e) => set({ title: e.target.value })} className="mt-1.5" /></div>
        <div><Label className="text-xs text-zinc-500">Description</Label>
          <Textarea rows={2} value={doc.description || ""} onChange={(e) => set({ description: e.target.value })} className="mt-1.5 resize-none" /></div>
      </Section>
      <Section title="Questions">
        <div className="space-y-3">
          {(doc.items || []).map((q, i) => (
            <div key={i} className="border border-zinc-200 rounded-md p-3 bg-[#FAFAFA] space-y-2">
              <div className="flex justify-end">
                <button type="button" onClick={() => removeItem(i)} className="text-zinc-400 hover:text-red-600 h-6 w-6 grid place-items-center">
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              </div>
              <Input value={q.question || ""} onChange={(e) => updateItem(i, "question", e.target.value)} placeholder="Question" className="font-medium" />
              <Textarea value={q.answer || ""} onChange={(e) => updateItem(i, "answer", e.target.value)} placeholder="Answer" rows={2} className="resize-none" />
            </div>
          ))}
          <Button type="button" variant="outline" onClick={addItem} className="w-full rounded-md border-dashed">
            <Plus className="h-4 w-4 mr-2" /> Add question
          </Button>
        </div>
      </Section>
    </>
  );
}

function PixelFields({ doc, set, kind }) {
  return (
    <>
      <Section title="Pixel">
        <div><Label className="text-xs text-zinc-500">Name</Label>
          <Input value={doc.name || ""} onChange={(e) => set({ name: e.target.value })} className="mt-1.5" /></div>
      </Section>
      {kind === "ga_pixel" ? (
        <Section title="GA4 Credentials">
          <div><Label className="text-xs text-zinc-500">Measurement ID</Label>
            <Input value={doc.ga_measurement_id || ""} onChange={(e) => set({ ga_measurement_id: e.target.value })}
              className="mt-1.5 font-mono" placeholder="G-XXXXXXXXXX" /></div>
          <div><Label className="text-xs text-zinc-500">
              API Secret {doc.has_api_secret ? <span className="text-zinc-400">(set — leave blank to keep)</span> : null}
            </Label>
            <Input type="password" value={doc.ga_api_secret || ""} onChange={(e) => set({ ga_api_secret: e.target.value })}
              className="mt-1.5 font-mono" /></div>
        </Section>
      ) : null}
    </>
  );
}

function RichTextFields({ doc, set }) {
  return (
    <>
      <Section title="Heading">
        <div><Label className="text-xs text-zinc-500">Title</Label>
          <Input value={doc.title || ""} onChange={(e) => set({ title: e.target.value })} className="mt-1.5" /></div>
        <div><Label className="text-xs text-zinc-500">Description</Label>
          <Textarea rows={2} value={doc.description || ""} onChange={(e) => set({ description: e.target.value })} className="mt-1.5 resize-none" /></div>
      </Section>
      <div className="space-y-3">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">Content</div>
        <RichTextEditor value={doc.content_html || ""} onChange={(v) => set({ content_html: v })} />
      </div>
    </>
  );
}

function GalleryFields({ doc, set, setItems, mediaMap, setMediaMap }) {
  const onUploaded = (m) => {
    setMediaMap((prev) => ({ ...prev, [m.id]: m }));
    setItems((prev) => [...(prev || []), { media_id: m.id, caption: "", link: "" }]);
  };
  const move = (i, dir) => {
    setItems((prev) => {
      const copy = [...(prev || [])];
      const j = i + dir;
      if (j < 0 || j >= copy.length) return copy;
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  };
  const remove = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));
  const update = (i, field, value) =>
    setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, [field]: value } : it));

  return (
    <>
      <Section title="Heading">
        <div><Label className="text-xs text-zinc-500">Title</Label>
          <Input value={doc.title || ""} onChange={(e) => set({ title: e.target.value })} className="mt-1.5" /></div>
        <div><Label className="text-xs text-zinc-500">Description</Label>
          <Textarea rows={2} value={doc.description || ""} onChange={(e) => set({ description: e.target.value })} className="mt-1.5 resize-none" /></div>
      </Section>
      <Section title="Layout">
        <div className="grid grid-cols-2 gap-3">
          {["grid", "slider"].map((l) => (
            <button key={l} type="button" onClick={() => set({ layout: l })}
              className={`p-3 rounded-md border text-left text-sm capitalize ${
                (doc.layout || "grid") === l ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 hover:border-zinc-300"
              }`}>{l}</button>
          ))}
        </div>
      </Section>
      <Section title="Photos">
        <MediaUploadButton onUploaded={onUploaded} />
        <div className="space-y-3 mt-3">
          {(doc.items || []).map((it, i) => {
            const m = mediaMap[it.media_id];
            return (
              <div key={i} className="flex gap-3 items-start p-3 rounded-md border border-zinc-200 bg-[#FAFAFA]">
                <div className="text-zinc-300"><GripVertical className="h-4 w-4" /></div>
                <div className="h-16 w-24 rounded overflow-hidden bg-zinc-100 flex-shrink-0">
                  {m?.url ? <img src={m.url} alt="" className="h-full w-full object-cover" /> : null}
                </div>
                <div className="flex-1 space-y-2 min-w-0">
                  <Input value={it.caption || ""} onChange={(e) => update(i, "caption", e.target.value)} placeholder="Caption" className="h-8 text-sm" />
                  <Input value={it.link || ""} onChange={(e) => update(i, "link", e.target.value)} placeholder="Link" className="h-8 text-sm font-mono" />
                </div>
                <div className="flex flex-col gap-0.5">
                  <button type="button" onClick={() => move(i, -1)} className="h-7 w-7 grid place-items-center rounded hover:bg-zinc-100 disabled:opacity-30" disabled={i === 0}><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => move(i, 1)} className="h-7 w-7 grid place-items-center rounded hover:bg-zinc-100 disabled:opacity-30" disabled={i === (doc.items.length - 1)}><ArrowDown className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => remove(i)} className="h-7 w-7 grid place-items-center rounded text-zinc-400 hover:text-red-600 hover:bg-red-50"><TrashIcon className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            );
          })}
        </div>
      </Section>
    </>
  );
}
